import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";

export type LocationSource = "manual" | "gps" | "default" | "";

export interface UserLocation {
    city: string;
    latitude: number;
    longitude: number;
    timezone: string;
    source: LocationSource;
    updated: string;
}

export interface LocationDraft {
    city: string;
    latitude: number;
    longitude: number;
    timezone?: string;
    source: "manual" | "gps";
}

export interface PlaceResult {
    city: string;
    label: string;
    region: string;
    country: string;
    latitude: number;
    longitude: number;
    timezone?: string;
}

/** The distance in kilometres between two coordinates. */
function distanceKm(latitude1: number, longitude1: number,
                   latitude2: number, longitude2: number): number {
    const radians = Math.PI / 180;
    const latitudeDelta = (latitude2 - latitude1) * radians;
    const longitudeDelta = (longitude2 - longitude1) * radians;
    const first = Math.sin(latitudeDelta / 2) ** 2
        + Math.cos(latitude1 * radians) * Math.cos(latitude2 * radians)
        * Math.sin(longitudeDelta / 2) ** 2;
    return 2 * 6371 * Math.atan2(Math.sqrt(first), Math.sqrt(Math.max(0, 1 - first)));
}

async function geolocationPermission(): Promise<PermissionState | "unknown"> {
    if (!navigator.permissions?.query) {
        return "unknown";
    }
    try {
        const status = await navigator.permissions.query({ name: "geolocation" });
        return status.state;
    } catch {
        return "unknown";
    }
}

function currentCoordinates(): Promise<{ latitude: number; longitude: number }> {
    if (!window.isSecureContext || !navigator.geolocation) {
        return Promise.reject(new Error("insecure"));
    }
    return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition((position) => {
            resolve({
                latitude: Math.round(position.coords.latitude * 1e6) / 1e6,
                longitude: Math.round(position.coords.longitude * 1e6) / 1e6,
            });
        }, reject, { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 });
    });
}

async function reversePlace(latitude: number, longitude: number): Promise<PlaceResult> {
    const place = await api.raw<PlaceResult>(
        `/me/location/reverse?lat=${latitude}&lon=${longitude}`);
    if (!place.data?.city) {
        throw new Error("no-city");
    }
    return place.data;
}

/** The location the account saved for the home widgets. The server returns
 *  Riyadh as a transient default when nothing is saved. When the browser
 *  already allows geolocation, the saved device location is refreshed silently
 *  after travel; a manual choice is never replaced automatically. */
export function useUserLocation({ autoDetect = true }: { autoDetect?: boolean } = {}) {
    const client = useQueryClient();
    const attempted = useRef(false);
    const query = useQuery<{ data: UserLocation }>({
        queryKey: ["me", "location"],
        queryFn: () => api.raw<UserLocation>("/me/location"),
        staleTime: 5 * 60 * 1000,
    });

    async function save(values: LocationDraft) {
        const payload = await api.patch<UserLocation>("/me/location", values);
        client.setQueryData(["me", "location"], payload);
        return payload.data;
    }

    useEffect(() => {
        if (!autoDetect || !query.data || attempted.current) {
            return;
        }
        const current = query.data.data;
        attempted.current = true;

        async function detect() {
            const permission = await geolocationPermission();
            if (permission === "denied") {
                return;
            }
            if (current.source !== "gps" && current.source !== "default") {
                return;
            }
            // A device location refreshed after travel should be silent. The
            // first default location may ask once; a manual choice never does.
            if (current.source === "gps" && permission !== "granted") {
                return;
            }
            try {
                const coordinates = await currentCoordinates();
                if (current.source === "gps" && current.city
                    && distanceKm(current.latitude, current.longitude,
                                  coordinates.latitude, coordinates.longitude) < 50) {
                    return;
                }
                const place = await reversePlace(coordinates.latitude, coordinates.longitude);
                await save({
                    city: place.city,
                    latitude: coordinates.latitude,
                    longitude: coordinates.longitude,
                    timezone: place.timezone,
                    source: "gps",
                });
            } catch {
                // Keep the saved or default location when the browser cannot help.
            }
        }

        void detect();
    }, [autoDetect, query.data]);

    return {
        location: query.data?.data ?? null,
        loading: query.isLoading,
        error: query.error,
        save,
        refresh: () => client.invalidateQueries({ queryKey: ["me", "location"] }),
    };
}
